import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavState } from '../hooks/useNavState';
import Header from './common/Header';
import Footer from './common/Footer';
import './Landing.css';

// Platform icons as inline SVGs for better quality and styling

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="overflow-hidden bg-gray-800 border border-gray-700 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-6 py-4 text-left transition-colors duration-200 hover:bg-gray-750"
      >
        <h3 className="pr-4 text-lg font-semibold text-white">{question}</h3>
        <div
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <svg
            className="w-5 h-5 text-[#9ec6f0]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-4">
          <p className="leading-relaxed text-gray-300">{answer}</p>
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const { navigateTo } = useNavState();

  const handleStartWatching = () => {
    // Navigate to the main app homepage
    navigateTo('/');
  };

  const handleWatchLive = () => {
    // Navigate to live TV page
    navigateTo('/live-tv');
  };

  const handleWatchOnDemand = () => {
    // Navigate to homepage
    navigateTo('/');
  };

  return (
    <div className="min-h-screen overflow-x-hidden text-white bg-black">
      <Header />

      {/* Hero Section - static ElectricNOW creative */}
      <section className="relative w-full pt-16 sm:pt-0">
        <div
          className="relative w-full h-[60vh] sm:h-[70vh] lg:h-screen overflow-hidden"
          aria-label="ElectricNOW Free Streaming TV"
        >
          <img
            src="/assets/images/electric-now-landing-hero.jpg"
            alt="ElectricNOW Free Streaming TV hero artwork"
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
          <div className="absolute inset-0 flex items-center px-6 sm:px-8">
            <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:items-center justify-between gap-6 lg:gap-8">
              {/* Logo on the left */}
              <div className="flex-shrink-0 w-full lg:w-auto">
                <img
                  src="/assets/images/electric-now-hero-overlay.png"
                  alt="ElectricNOW Free Streaming TV"
                  className="w-full max-w-2xl drop-shadow-2xl mx-auto lg:mx-0"
                />
              </div>
              
              {/* CTA Buttons on the right */}
              <div className="flex flex-col sm:flex-row lg:flex-col gap-4 flex-shrink-0 w-full sm:w-auto lg:w-auto">
                <button
                  onClick={handleWatchLive}
                  className="px-8 py-4 text-lg sm:text-xl font-bold tracking-wider text-white uppercase transition-all duration-300 bg-[#2f6eb0] rounded-lg hover:scale-105 hover:shadow-2xl hover:bg-[#3d7fcb] w-full sm:w-auto sm:min-w-[200px] lg:min-w-[250px]"
                >
                  Watch Live
                </button>
                <button
                  onClick={handleWatchOnDemand}
                  className="px-8 py-4 text-lg sm:text-xl font-bold tracking-wider text-white uppercase transition-all duration-300 bg-transparent border-2 border-white rounded-lg hover:scale-105 hover:shadow-2xl hover:bg-white hover:text-black w-full sm:w-auto sm:min-w-[200px] lg:min-w-[250px]"
                >
                  Watch On Demand
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-16 bg-black">
        <div className="px-6 mx-auto max-w-5xl">
          {/* Header */}
          <h2 className="mb-8 text-3xl md:text-4xl font-display tracking-wide text-center text-white leading-tight">
            ElectricNOW is Free Streaming TV for Escapist Entertainment and Cult Classics
          </h2>
          
          {/* Body Copy */}
          <div className="text-gray-300 leading-relaxed text-lg space-y-6">
            <p className="text-center">
              ElectricNOW is your go-to destination for Free Streaming TV that delivers cult-favorite shows, 
              thrilling movies, and deep characters that fans have come to love for decades. The best part 
              of all is that you can enjoy ElectricNOW without a subscription!
            </p>
            
            {/* Shows Featured */}
            <p className="text-center">
              <span className="text-white font-semibold">Shows featured:</span>{' '}
              <span className="italic">Leverage, Leverage: Redemption, The Librarians, The Outpost, The Ark, Almost Paradise</span>
              {' '}— curated by Dean Devlin.
            </p>
            
            {/* Available On */}
            <p className="text-center text-base text-gray-400">
              <span className="text-white font-semibold">Available on:</span>{' '}
              Roku, Apple TV/iOS, Amazon Fire TV, Android TV, Samsung/LG/Hisense/Vizio/Sony smart TVs, 
              Xfinity, mobile carriers, and web at{' '}
              <a href="https://www.electricnow.tv" className="text-[#2f6eb0] hover:underline">www.electricnow.tv</a>
            </p>
            
            {/* Why Viewers Love ElectricNOW */}
            <div className="mt-10">
              <h3 className="text-xl font-display tracking-wide text-white text-center mb-6">
                Why Viewers Love ElectricNOW
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Free, no login/subscription required</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Curated by Dean Devlin / Electric Entertainment</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Fantasy/sci-fi hits (The Librarians, The Ark)</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Escapist entertainment destination</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Cast interviews, BTS content, weekly marathons</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#2f6eb0] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Linear FAST channels (English + Spanish) + VOD</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-2xl md:text-3xl font-display tracking-wide text-white mb-6">
              Turn us on. Leave us on. Watch ElectricNOW
            </p>
            <button
              onClick={handleStartWatching}
              className="inline-flex items-center gap-2 px-10 py-4 font-display tracking-[0.2em] text-white transition-all duration-300 bg-[#2f6eb0] border border-[#2f6eb0] rounded-lg hover:bg-[#1e5a9a] hover:scale-105 uppercase text-lg"
            >
              Start Watching Now
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl px-6 mx-auto">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-display tracking-[0.2em] uppercase text-white md:text-5xl">
              Frequently Asked Questions
            </h2>
            <p className="max-w-2xl mx-auto text-xl text-gray-300">
              Everything you need to know about ElectricNOW
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Is ElectricNOW really free?',
                answer:
                  'Yes! ElectricNOW is completely free to use. We offer thousands of classic movies and TV shows at no cost. Our platform is supported by minimal, non-intrusive advertising that helps us keep the service free for everyone.',
              },
              {
                question: 'What kind of content is available?',
                answer:
                  'ElectricNOW specializes in classic movies and timeless TV shows from various decades. Our library includes drama, comedy, action, horror, documentaries, and more. We focus on quality content that has stood the test of time, all carefully curated by our AI-powered recommendation system.',
              },
              {
                question: 'Do I need to create an account?',
                answer:
                  'While you can browse some content without an account, creating a free profile unlocks the full ElectricNOW experience. With an account, you get personalized recommendations, watchlists, viewing history, and the ability to resume watching across devices.',
              },
              {
                question: 'What devices can I watch on?',
                answer:
                  'ElectricNOW works on virtually any device! Watch on your computer, smartphone, tablet, smart TV, Roku, Apple TV, Fire TV, Android TV, and more. Your viewing experience syncs seamlessly across all your devices.',
              },
              {
                question: 'How does the AI recommendation system work?',
                answer:
                  "Our advanced AI analyzes your viewing patterns, preferences, and ratings to suggest content you'll love. The more you watch, the smarter our recommendations become. We also use AI to enhance video quality and optimize streaming performance.",
              },
              {
                question: 'Is there a mobile app?',
                answer:
                  'Yes! ElectricNOW has dedicated apps for iOS and Android devices. Download them from the App Store or Google Play Store for the best mobile viewing experience with offline capabilities and push notifications.',
              },
              {
                question: 'Can I download content for offline viewing?',
                answer:
                  'Offline viewing is available on our mobile apps for select content. This feature allows you to download movies and shows to watch later without an internet connection - perfect for travel or areas with limited connectivity.',
              },
              {
                question: 'How often is new content added?',
                answer:
                  "We regularly add new classic movies and TV shows to our library. While we focus on timeless content rather than the latest releases, we're constantly expanding our collection with hidden gems and beloved classics you might have missed.",
              },
            ].map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="mb-6 text-gray-400">Still have questions? We're here to help!</p>
            <button
              onClick={() => navigateTo('/contact')}
              className="px-8 py-3 font-display tracking-[0.2em] text-white transition-all duration-300 bg-[#2f6eb0] rounded-lg hover:bg-[#3d7fcb] hover:scale-105 uppercase"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-black border-t border-b border-gray-800">
        <div className="max-w-6xl px-6 mx-auto">
          <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
            {[
              { number: '1000+', label: 'Classic Titles' },
              { number: '24/7', label: 'Live Channels' },
              { number: '100%', label: 'AI-Powered' },
              { number: 'FREE', label: 'Always' },
            ].map((stat, index) => (
              <div key={index} className="p-8">
                <div className="mb-2 text-6xl font-display tracking-[0.1em] text-[#9ec6f0] drop-shadow-lg">
                  {stat.number}
                </div>
                <div className="text-xl font-display tracking-[0.25em] text-gray-300 uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl px-6 mx-auto text-center">
          <h2 className="mb-8 text-5xl font-display tracking-[0.25em] uppercase text-white">Watch Anywhere</h2>
          <p className="max-w-3xl mx-auto mb-16 text-xl text-gray-300">
            Stream seamlessly across all your devices. Download ElectricNOW on your favorite platform.
          </p>

          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5">
            {/* Apple App Store */}
            <a
              href="https://itunes.apple.com/us/app/id1501030946"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl transition-all duration-300 hover:border-[#2f6eb0] hover:bg-gray-700/50 hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-16 h-16 mb-4">
                <svg className="w-12 h-12 text-white group-hover:text-[#2f6eb0] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="text-sm font-medium text-center text-white">App Store</div>
              <div className="text-xs text-gray-400 mt-1">iPhone & iPad</div>
            </a>

            {/* Google Play Store */}
            <a
              href="https://play.google.com/store/apps/details?id=com.electric.now"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl transition-all duration-300 hover:border-[#2f6eb0] hover:bg-gray-700/50 hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-16 h-16 mb-4">
                <svg className="w-12 h-12 text-white group-hover:text-[#2f6eb0] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </div>
              <div className="text-sm font-medium text-center text-white">Google Play</div>
              <div className="text-xs text-gray-400 mt-1">Android</div>
            </a>

            {/* Roku */}
            <a
              href="https://channelstore.roku.com/details/294381/electricnow"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl transition-all duration-300 hover:border-[#2f6eb0] hover:bg-gray-700/50 hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-16 h-16 mb-4">
                <svg className="w-12 h-12 text-white group-hover:text-[#2f6eb0] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="text-sm font-medium text-center text-white">Roku</div>
              <div className="text-xs text-gray-400 mt-1">Channel Store</div>
            </a>

            {/* Apple TV */}
            <a
              href="https://itunes.apple.com/us/app/id1501030946"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl transition-all duration-300 hover:border-[#2f6eb0] hover:bg-gray-700/50 hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-16 h-16 mb-4">
                <svg className="w-12 h-12 text-white group-hover:text-[#2f6eb0] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 17H3V5h18m0-2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                  <path d="M12 8.5c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1s1-.45 1-1v-3c0-.55-.45-1-1-1zm-3 1c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1zm6 0c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1z"/>
                </svg>
              </div>
              <div className="text-sm font-medium text-center text-white">Apple TV</div>
              <div className="text-xs text-gray-400 mt-1">tvOS App</div>
            </a>

            {/* Android TV / Fire TV */}
            <a
              href="https://play.google.com/store/apps/details?id=com.electric.now.tv"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl transition-all duration-300 hover:border-[#2f6eb0] hover:bg-gray-700/50 hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-16 h-16 mb-4">
                <svg className="w-12 h-12 text-white group-hover:text-[#2f6eb0] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 17H3V5h18m0-2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                </svg>
              </div>
              <div className="text-sm font-medium text-center text-white">Android TV</div>
              <div className="text-xs text-gray-400 mt-1">& Fire TV</div>
            </a>
          </div>

          {/* More Devices Link */}
          <div className="mt-10">
            <Link
              to="/devices"
              className="inline-flex items-center gap-2 text-[#b3cfed] hover:text-white transition-colors text-lg"
            >
              <span>View all supported devices</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-[#2f6eb0] to-[#3d7fcb]">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-4xl px-6 mx-auto text-center">
          <h2 className="mb-8 text-5xl font-display tracking-[0.25em] uppercase text-black md:text-6xl">
            Ready to Experience ElectricNOW?
          </h2>
          <p className="max-w-3xl mx-auto mb-12 text-2xl text-black/80">
            Join the streaming revolution. Thousands of classic movies and shows await, powered by
            the world's most advanced AI technology.
          </p>
          <button
            onClick={handleStartWatching}
            className="px-12 py-6 text-2xl font-bold tracking-wider text-[#9ec6f0] uppercase transition-all duration-300 bg-black rounded-lg hover:scale-105 hover:shadow-2xl hover:bg-gray-900"
          >
            Start Watching Now
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;

